import { splitExtension, toE164 } from '~/lib/phone';
import {
  canonicalEnumValue,
  CSV_COMMENT_PREFIX,
  joinList,
  sameLabel,
  splitList,
  type CsvField,
  type CsvSchema
} from './schema';

// Handles the Excel dialect only: quoted fields, embedded commas and newlines, doubled
// quotes, a BOM, and CRLF.

export type ParsedRow = {
  /** 1-based line in the uploaded file, matching what the user sees in Excel. */
  line: number;
  cells: string[];
};

export type ParsedCsv = {
  /** Column names from the first non-comment line. Empty when the file has none. */
  header: string[];
  rows: ParsedRow[];
};

/** Excel writes a BOM on UTF-8 export; left in, it would corrupt the first column name. */
const stripBom = (text: string): string => (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text);

/** Splits `text` into rows of cells. A trailing newline does not open an empty final row. */
const splitRows = (text: string): string[][] => {
  const rows: string[][] = [];
  let cells: string[] = [];
  let cell = '';
  let quoted = false;
  let started = false;

  const endCell = () => {
    cells.push(cell);
    cell = '';
  };
  const endRow = () => {
    endCell();
    rows.push(cells);
    cells = [];
    started = false;
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    started = true;

    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      endCell();
    } else if (char === '\n') {
      endRow();
    } else if (char !== '\r') {
      // CR-only line endings are not supported.
      cell += char;
    }
  }

  if (started || cell !== '' || cells.length > 0) {
    endRow();
  }
  return rows;
};

const isBlank = (cells: string[]): boolean => cells.every((cell) => cell.trim() === '');

const isComment = (cells: string[]): boolean =>
  cells[0]?.trimStart().startsWith(CSV_COMMENT_PREFIX) ?? false;

/**
 * Reads `text` as CSV, dropping blank and commented lines. Line numbers still count
 * dropped lines so errors match the user's spreadsheet.
 */
export const parseCsv = (text: string): ParsedCsv => {
  const rows = splitRows(stripBom(text));
  let header: string[] = [];
  const parsed: ParsedRow[] = [];

  rows.forEach((cells, index) => {
    if (isBlank(cells) || isComment(cells)) {
      return;
    }
    if (header.length === 0) {
      header = cells.map((cell) => cell.trim());
      return;
    }
    parsed.push({ line: index + 1, cells });
  });

  return { header, rows: parsed };
};

/** A lone '+' is what's left of a deleted country code, so it counts as empty. */
export const tidyPhone = (text: string) => (text === '+' ? '' : (toE164(text) ?? text));

/** A cell in the form it will be stored in, where that needs no guessing. */
const cleanCell = (field: CsvField | undefined, raw: string): string => {
  const cell = raw.trim();
  if (field?.type === 'string' && !field.compareLiterally) {
    return cell.replace(/[ \t]+/g, ' ');
  }
  if (field?.type === 'phone') {
    return tidyPhone(cell);
  }
  if (field?.type === 'enum') {
    if (!field.multiple) {
      return canonicalEnumValue(field, cell) ?? cell;
    }
    const values = splitList(cell);
    const canonical = values.map((value) => canonicalEnumValue(field, value) ?? value);
    return canonical.some((value, i) => value !== values[i]) ? joinList(canonical) : cell;
  }
  return cell;
};

/** "ext204", "x 204": a marker in the extension's own column, before what is dialled. */
const EXTENSION_MARKER = /^\s*(?:extension|ext\.?|x)\s*(?=[\d*#])/i;

const withoutExtensionMarker = (row: ParsedRow, column: number): ParsedRow => {
  const cell = row.cells[column] ?? '';
  const stripped = cell.replace(EXTENSION_MARKER, '');
  return stripped === cell
    ? row
    : { ...row, cells: row.cells.map((value, index) => (index === column ? stripped : value)) };
};

/** Moves "212 555 0150 x204" into `extensionField`; a value already there wins. */
const moveExtensions = (schema: CsvSchema, parsed: ParsedCsv): ParsedCsv => {
  let { header, rows } = parsed;
  for (const phone of schema.fields) {
    const target = schema.fields.find((field) => field.key === phone.extensionField);
    const phoneAt = header.findIndex((cell) => sameLabel(cell, phone.label));
    if (phone.type !== 'phone' || !target || phoneAt === -1) {
      continue;
    }
    let targetAt = header.findIndex((cell) => sameLabel(cell, target.label));
    rows = rows.map((unmarked) => {
      const row = targetAt === -1 ? unmarked : withoutExtensionMarker(unmarked, targetAt);
      const split = splitExtension(row.cells[phoneAt] ?? '');
      const existing = targetAt === -1 ? '' : (row.cells[targetAt] ?? '').trim();
      if (!split) {
        return row;
      }
      if (targetAt === -1) {
        header = [...header, target.label];
        targetAt = header.length - 1;
      }
      const cells = [...row.cells];
      cells[phoneAt] = split.number;
      cells[targetAt] = existing || split.extension;
      return { ...row, cells: Array.from(cells, (cell) => cell ?? '') };
    });
  }
  return { header, rows };
};

/** Parses and normalises values on the way in, so the grid shows each value as it will be saved. */
export const parseForSchema = (schema: CsvSchema, text: string): ParsedCsv => {
  const parsed = moveExtensions(schema, parseCsv(text));
  const fields = parsed.header.map((label) =>
    schema.fields.find((candidate) => sameLabel(candidate.label, label))
  );
  return {
    header: parsed.header,
    rows: parsed.rows.map((row) => ({
      ...row,
      cells: row.cells.map((cell, index) => cleanCell(fields[index], cell))
    }))
  };
};
